/**
 * BESTIX v5.0 — Last Minute Card
 * Gametime-style deal card with countdown
 */

import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Zap, MapPin, TrendingDown } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface LastMinuteCardProps {
  id: string;
  title: string;
  venue: string;
  location: string;
  eventDateTime: string;
  image: string;
  originalPrice: number;
  dealPrice: number;
  seatsAvailable: number;
  section?: string;
}

function useCountdown(targetDateTime: string) {
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, isExpired: false, isUrgent: false });
  
  // Memoize the Date object to prevent new reference on every render
  const targetDate = useMemo(() => new Date(targetDateTime), [targetDateTime]);

  useEffect(() => {
    const calculate = () => {
      const now = new Date().getTime();
      const diff = targetDate.getTime() - now;

      if (diff <= 0) {
        return { hours: 0, minutes: 0, isExpired: true, isUrgent: false };
      }

      const totalHours = diff / (1000 * 60 * 60);
      return {
        hours: Math.floor(totalHours),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        isExpired: false,
        isUrgent: totalHours < 2,
      };
    };

    setTimeLeft(calculate());
    const timer = setInterval(() => setTimeLeft(calculate()), 1000);
    return () => clearInterval(timer);
  }, [targetDate]);

  return timeLeft;
}

export function LastMinuteCard({
  id,
  title,
  venue,
  location,
  eventDateTime,
  image,
  originalPrice,
  dealPrice,
  seatsAvailable,
  section,
}: LastMinuteCardProps) {
  const { hours, minutes, isExpired, isUrgent } = useCountdown(eventDateTime);

  const discountPercent = Math.round(((originalPrice - dealPrice) / originalPrice) * 100);

  if (isExpired) return null;

  return (
    <Link
      to={`/events/${id}`}
      className="group block bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100"
    >
      {/* Image with Overlay */}
      <div className="relative aspect-[16/9]">
        <img src={image} alt={title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-black/10" />

        {/* Discount Badge */}
        <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 bg-[var(--color-coral)] text-white rounded-full text-sm font-bold">
          <TrendingDown className="w-4 h-4" />
          {discountPercent}% OFF
        </div>

        {/* Countdown */}
        <div
          className={cn(
            'absolute top-3 right-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-medium',
            isUrgent ? 'bg-[var(--color-coral)] text-white animate-pulse' : 'bg-white/90 text-[var(--color-navy)]'
          )}
        >
          <Clock className="w-4 h-4" />
          {hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`}
        </div>

        {/* Event Info */}
        <div className="absolute bottom-3 left-3 right-3 text-white">
          <p className="text-xs uppercase tracking-wide text-white/70 flex items-center gap-1">
            <Zap className="w-3 h-3" />
            {isUrgent ? 'STARTS SOON' : 'TONIGHT'}
          </p>
          <h3 className="font-playfair text-lg line-clamp-1 mt-1">{title}</h3>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
          <MapPin className="w-4 h-4 flex-shrink-0" />
          <span className="truncate">{venue} • {location}</span>
        </div>

        {section && (
          <p className="text-sm text-gray-600 mb-3">
            Section {section} • {seatsAvailable} {seatsAvailable === 1 ? 'seat' : 'seats'} left
          </p>
        )}

        <div className="flex items-end justify-between">
          <div>
            <p className="text-sm text-gray-400 line-through">${(originalPrice / 100).toFixed(0)}</p>
            <p className="text-2xl font-bold text-[var(--color-coral)]">${(dealPrice / 100).toFixed(0)}</p>
          </div>
          <button className="px-4 py-2 bg-[var(--color-coral)] text-white rounded-full text-sm font-medium group-hover:bg-[var(--color-coral-dark)] transition-colors">
            Grab Deal
          </button>
        </div>
      </div>
    </Link>
  );
}

export default LastMinuteCard;
